import React, { useState, useEffect } from 'react';
import { useAppDispatch } from '../store/hooks';
import Sidebar from '../components/Sidebar';
import axios from 'axios';
import common from '../utils/common';
import { authStorage } from '../utils/authStorage';
import './ntc-branding.css';

const AllocationSheet = () => {
  const dispatch = useAppDispatch();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [project, setProject] = useState(null);
  const [interests, setInterests] = useState([]);
  const [totalAllocation, setTotalAllocation] = useState(0);
  const [totalCommitment, setTotalCommitment] = useState(0);
  const [loginData, setLoginData] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isMakingActive, setIsMakingActive] = useState(false);

  // Custom Alert State
  const [customAlert, setCustomAlert] = useState({
    show: false,
    message: '',
    type: 'error'
  });

  const showAlert = (message, type = 'error') => {
    setCustomAlert({ show: true, message, type });
    setTimeout(() => {
      setCustomAlert({ show: false, message: '', type: 'error' });
    }, type === 'success' ? 4000 : 5000);
  };

  const hideAlert = () => {
    setCustomAlert({ show: false, message: '', type: 'error' });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
    document.documentElement.classList.toggle('minimenu');
    localStorage.setItem('nexel-classic-dashboard-menu-mini-theme', !sidebarCollapsed ? 'menu-mini-theme' : '');
  };

  // Restore sidebar state from localStorage on component mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('nexel-classic-dashboard-menu-mini-theme');
    if (savedTheme === 'menu-mini-theme') {
      setSidebarCollapsed(true);
      document.documentElement.classList.add('minimenu');
    } else {
      setSidebarCollapsed(false);
      document.documentElement.classList.remove('minimenu');
    }
  }, []);

  useEffect(() => {
    const data = authStorage.getLoginData();
    if (!data) return;
    if (authStorage.getDirectLink() === '1') {
      window.location.href = '/';
      return;
    }
    setLoginData(data);

    // Get project ID from URL query parameter
    const urlParams = new URLSearchParams(window.location.search);
    const projectId = urlParams.get('project');

    if (!projectId) {
      showAlert('Project ID is required', 'error');
      setTimeout(() => {
        window.location.href = '/upcoming';
      }, 2000);
      return;
    }

    // Fetch project and interests
    fetchProjectAndInterests(projectId, loginData.data);
  }, []);

  const fetchProjectAndInterests = async (projectId, userData) => {
    try {
      const baseUrl = common.Data.API_URL;
      
      // Fetch project details
      const projectResponse = await axios.get(`${baseUrl}/v1/get-projects/upcoming-${userData?.id || 1}-${userData?.role || 1}`);
      const projects = projectResponse.data;
      const selectedProject = projects.find(p => p.id == projectId);
      
      if (!selectedProject) {
        showAlert('Project not found', 'error');
        setTimeout(() => {
          window.location.href = '/upcoming';
        }, 2000);
        return;
      }

      setProject(selectedProject);
      setTotalAllocation(parseInt(selectedProject.allocation_available || 0));

      // Fetch interests/commitments
      const interestsResponse = await axios.get(`${baseUrl}/v1/get-commitment/${projectId}`);
      const interestsData = interestsResponse.data || [];
      
      // Initialize allocation_amount with amount for each interest
      const initializedInterests = interestsData.map(intr => ({
        ...intr,
        allocation_amount: intr.amount || 0
      }));

      setInterests(initializedInterests);
      
      // Calculate initial total commitment
      const initialCommitment = initializedInterests.reduce((sum, intr) => {
        return sum + parseInt(intr.amount || 0);
      }, 0);
      setTotalCommitment(initialCommitment);
    } catch (error) {
      console.error('Error fetching project and interests:', error);
      showAlert('Failed to fetch project details', 'error');
    }
  };

  const divideEqually = () => {
    if (!interests.length || !totalAllocation) return;

    const fundParticipantIds = [2, 52]; // Special participants
    const otherParticipants = interests.filter(intr => 
      !fundParticipantIds.includes(intr.participant_id)
    );
    const otherParticipantCount = otherParticipants.length;

    if (otherParticipantCount === 0) {
      showAlert('No participants available for equal distribution', 'warning');
      return;
    }

    const share = Math.ceil((totalAllocation) / otherParticipantCount);
    let totalCommit = 0;

    const updatedInterests = interests.map(intr => {
      if (fundParticipantIds.includes(intr.participant_id)) {
        totalCommit += parseInt(intr.amount || 0);
        return { ...intr, allocation_amount: intr.amount || 0 };
      } else {
        totalCommit += share;
        return { ...intr, allocation_amount: share };
      }
    });

    setInterests(updatedInterests);
    setTotalCommitment(totalCommit);
  };

  const ascendingDistribution = () => {
    if (!interests.length || !totalAllocation) return;

    const fundParticipantIds = [2, 52]; // Special participants
    const sortedInterests = [...interests].sort((a, b) => parseInt(a.amount || 0) - parseInt(b.amount || 0));
    const otherParticipantCount = sortedInterests.filter(intr => 
      !fundParticipantIds.includes(intr.participant_id)
    ).length;

    let remainingShare = totalAllocation;
    let previousMin = 0;
    const allocationAmounts = [];

    // Initialize allocation amounts
    sortedInterests.forEach(intr => {
      if (fundParticipantIds.includes(intr.participant_id)) {
        allocationAmounts.push(-1); // Special marker for fund participants
        remainingShare -= parseInt(intr.amount || 0);
      } else {
        allocationAmounts.push(0);
      }
    });

    // Distribute ascendingly
    for (let i = 0; i < sortedInterests.length; i++) {
      const intr = sortedInterests[i];
      
      if (fundParticipantIds.includes(intr.participant_id)) {
        continue; // Skip fund participants
      }

      if (i === 0) {
        previousMin = parseInt(intr.amount || 0);
        remainingShare -= previousMin;
        allocationAmounts.forEach((amt, idx) => {
          if (amt !== -1) {
            allocationAmounts[idx] = previousMin;
          }
        });
      } else {
        const currentAmount = parseInt(intr.amount || 0);
        if (currentAmount !== previousMin) {
          const remainingPeople = otherParticipantCount - i;
          if (currentAmount * remainingPeople <= remainingShare) {
            previousMin = currentAmount;
            allocationAmounts.forEach((amt, idx) => {
              if (idx >= i && amt !== -1) {
                allocationAmounts[idx] = Math.min(currentAmount, amt + previousMin);
              }
            });
            remainingShare -= currentAmount;
          } else {
            const share = Math.ceil(remainingShare / remainingPeople);
            allocationAmounts.forEach((amt, idx) => {
              if (idx >= i && amt !== -1) {
                allocationAmounts[idx] = share;
              }
            });
            break;
          }
        } else {
          remainingShare -= currentAmount;
        }
      }
    }

    // Map back to original order
    let totalCommit = 0;
    const updatedInterests = interests.map(intr => {
      const sortedIndex = sortedInterests.findIndex(si => si.participant_id === intr.participant_id);
      if (fundParticipantIds.includes(intr.participant_id)) {
        totalCommit += parseInt(intr.amount || 0);
        return { ...intr, allocation_amount: intr.amount || 0 };
      } else {
        const allocatedAmount = allocationAmounts[sortedIndex] || 0;
        totalCommit += allocatedAmount;
        return { ...intr, allocation_amount: allocatedAmount };
      }
    });

    setInterests(updatedInterests);
    setTotalCommitment(totalCommit);
  };

  const handleAllocationChange = (participantId, value) => {
    const updatedInterests = interests.map(intr => {
      if (intr.participant_id === participantId) {
        return { ...intr, allocation_amount: value || 0 };
      }
      return intr;
    });

    const newTotalCommitment = updatedInterests.reduce((sum, intr) => {
      return sum + parseInt(intr.allocation_amount || 0);
    }, 0);

    setInterests(updatedInterests);
    setTotalCommitment(newTotalCommitment);
  };

  const saveAllocation = async () => {
    if (totalAllocation !== totalCommitment) {
      showAlert('Commitment Mismatch! Total allocation must equal total commitment.', 'error');
      return;
    }

    setIsSaving(true);
    try {
      const baseUrl = common.Data.API_URL;
      
      // Prepare data for API
      const allocationData = interests.map(intr => ({
        proj_id: project.id,
        part_id: intr.participant_id,
        amount: intr.amount,
        allocation_amount: intr.allocation_amount,
        wallet_add: intr.wallet_address
      }));

      await axios.post(`${baseUrl}/v1/final-allocation`, allocationData);
      showAlert('Allocation saved successfully!', 'success');
    } catch (error) {
      console.error('Error saving allocation:', error);
      showAlert(error.response?.data?.message || 'Failed to save allocation', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const makeActive = async () => {
    if (!project) return;

    if (totalAllocation !== totalCommitment) {
      showAlert('Please save allocation first before making the project active', 'warning');
      return;
    }

    setIsMakingActive(true);
    try {
      const baseUrl = common.Data.API_URL;
      await axios.get(`${baseUrl}/v1/make-active/${project.id}`);
      showAlert('Project activated successfully!', 'success');
      setTimeout(() => {
        window.location.href = '/upcoming';
      }, 2000);
    } catch (error) {
      console.error('Error making project active:', error);
      showAlert(error.response?.data?.message || 'Failed to activate project', 'error');
    } finally {
      setIsMakingActive(false);
    }
  };

  const allocationAvailable = totalAllocation - totalCommitment;

  if (!project) {
    return (
      <div className="nxl-wrapper">
        <Sidebar 
          sidebarCollapsed={sidebarCollapsed} 
          setSidebarCollapsed={setSidebarCollapsed} 
          toggleSidebar={toggleSidebar}
        />
        <main className="nxl-container" style={{ paddingTop: '0' }}>
          <div className="nxl-content" style={{ paddingTop: '0' }}>
            <div className="container-fluid py-4">
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="nxl-wrapper">
      {/* Custom Alert */}
      {customAlert.show && (
        <div 
          className="position-fixed"
          style={{
            top: '24px',
            right: '24px',
            zIndex: 9999,
            minWidth: '380px',
            maxWidth: '450px',
            animation: 'slideInRight 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
          }}
        >
          <div 
            className="card shadow-lg border-0"
            style={{
              borderRadius: '16px',
              overflow: 'hidden',
              border: `2px solid ${
                customAlert.type === 'error' ? '#dc3545' : 
                customAlert.type === 'success' ? '#28a745' : 
                customAlert.type === 'warning' ? '#f57c00' : 
                '#17a2b8'
              }`
            }}
          >
            <div 
              className={`card-body p-3 d-flex align-items-center position-relative`}
              style={{
                backgroundColor: '#ffffff',
                borderLeft: `5px solid ${
                  customAlert.type === 'error' ? '#dc3545' : 
                  customAlert.type === 'success' ? '#28a745' : 
                  customAlert.type === 'warning' ? '#f57c00' : 
                  '#17a2b8'
                }`
              }}
            >
              <div 
                className={`d-flex align-items-center justify-content-center me-3`}
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: customAlert.type === 'error' ? 'rgba(220, 53, 69, 0.15)' : 
                                   customAlert.type === 'success' ? 'rgba(40, 167, 69, 0.15)' : 
                                   customAlert.type === 'warning' ? 'rgba(255, 193, 7, 0.15)' : 
                                   'rgba(23, 162, 184, 0.15)',
                  flexShrink: 0,
                  boxShadow: `0 2px 8px rgba(0,0,0,0.08)`
                }}
              >
                <i 
                  className={`feather-${
                    customAlert.type === 'error' ? 'alert-circle' : 
                    customAlert.type === 'success' ? 'check-circle' : 
                    customAlert.type === 'warning' ? 'alert-triangle' : 
                    'info'
                  }`}
                  style={{ 
                    fontSize: '24px', 
                    color: customAlert.type === 'error' ? '#dc3545' : 
                           customAlert.type === 'success' ? '#28a745' : 
                           customAlert.type === 'warning' ? '#f57c00' : 
                           '#17a2b8'
                  }}
                ></i>
              </div>
              <div className="flex-grow-1">
                <h6 
                  className={`mb-1 fw-bold`}
                  style={{ fontSize: '16px', 
                           color: customAlert.type === 'error' ? '#dc3545' : 
                                  customAlert.type === 'success' ? '#28a745' : 
                                  customAlert.type === 'warning' ? '#f57c00' : 
                                  '#17a2b8'
                         }}
                >
                  {customAlert.type === 'error' ? 'Error' : 
                   customAlert.type === 'success' ? 'Success' : 
                   customAlert.type === 'warning' ? 'Warning' : 
                   'Information'}
                </h6>
                <p className="mb-0 text-dark" style={{ fontSize: '14px', lineHeight: '1.4' }}>
                  {customAlert.message}
                </p>
              </div>
              <button
                type="button"
                className="btn-close ms-2"
                onClick={hideAlert}
                aria-label="Close"
                style={{
                  opacity: 0.8,
                  transition: 'all 0.2s ease',
                  transform: 'scale(1)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.transform = 'scale(1.1)'}
                onMouseLeave={(e) => e.currentTarget.style.transform = 'scale(1)'}
              ></button>
            </div>
            <div 
              className="progress" 
              style={{ height: '4px', borderRadius: '0', backgroundColor: 'rgba(0,0,0,0.05)' }}
            >
              <div 
                className={`progress-bar ${
                  customAlert.type === 'error' ? 'bg-danger' : 
                  customAlert.type === 'success' ? 'bg-success' : 
                  customAlert.type === 'warning' ? 'bg-warning' : 
                  'bg-info'
                }`}
                role="progressbar" 
                style={{ width: '100%', animation: `progressBar ${customAlert.type === 'success' ? 4 : 5}s linear forwards` }} 
                aria-valuenow="100" 
                aria-valuemin="0" 
                aria-valuemax="100"
              ></div>
            </div>
          </div>
        </div>
      )}
      <style>{`
        @keyframes slideInRight {
          from {
            transform: translateX(100%) scale(0.9);
            opacity: 0;
          }
          to {
            transform: translateX(0) scale(1);
            opacity: 1;
          }
        }
        @keyframes progressBar {
          from { width: 100%; }
          to { width: 0%; }
        }
      `}</style>

      <Sidebar 
        sidebarCollapsed={sidebarCollapsed} 
        setSidebarCollapsed={setSidebarCollapsed} 
        toggleSidebar={toggleSidebar}
      />

      {/* Main Content */}
      <main className="nxl-container" style={{ paddingTop: '0' }}>
        <div className="nxl-content" style={{ paddingTop: '0' }}>
          {/* Page Header */}
          <div className="page-header">
            <div className="page-header-left d-flex align-items-center">
              {/* Sidebar Toggle Button */}
              <div className="nxl-navigation-toggle me-3">
                <a href="javascript:void(0);" id="menu-mini-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'none' : 'block' }}>
                  <i className="feather-align-left"></i>
                </a>
                <a href="javascript:void(0);" id="menu-expend-button" onClick={toggleSidebar} style={{ display: sidebarCollapsed ? 'block' : 'none' }}>
                  <i className="feather-arrow-right"></i>
                </a>
              </div>
              <div className="page-header-title">
                <h5 className="m-b-10">Allocation Sheet</h5>
              </div>
              <ul className="breadcrumb">
                <li className="breadcrumb-item"><a href="/investor-dashboard">Home</a></li>
                <li className="breadcrumb-item"><a href="/upcoming">Upcoming Projects</a></li>
                <li className="breadcrumb-item">Allocation Sheet</li>
              </ul>
            </div>
            <div className="page-header-right ms-auto d-flex align-items-center">
              {loginData && (
                <div className="d-flex align-items-center gap-2">
                  <i className="feather-user text-primary"></i>
                  <span className="fw-semibold text-dark" style={{ fontSize: '14px' }}>
                    Welcome, {loginData.name || 'User'}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="container-fluid py-4">
            {/* Stats Cards */}
            <div className="row mb-4">
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#e3f2fd' }}>
                        <i className="feather-dollar-sign text-primary" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Total Allocation</div>
                        <div className="fw-bold text-dark" style={{ fontSize: '18px' }}>
                          {totalAllocation.toLocaleString()} {project.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className="rounded-circle d-flex align-items-center justify-content-center" style={{ width: '48px', height: '48px', backgroundColor: '#fff3e0' }}>
                        <i className="feather-trending-up text-warning" style={{ fontSize: '20px' }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>Allocation Used</div>
                        <div className="fw-bold text-warning" style={{ fontSize: '18px' }}>
                          {totalCommitment.toLocaleString()} {project.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-sm-6 col-lg-3 mb-3">
                <div className="card shadow-sm border-0 h-100">
                  <div className="card-body">
                    <div className="d-flex align-items-center gap-3">
                      <div className={`rounded-circle d-flex align-items-center justify-content-center`} 
                           style={{ 
                             width: '48px', 
                             height: '48px', 
                             backgroundColor: allocationAvailable < 0 ? '#ffebee' : '#e8f5e9'
                           }}>
                        <i className={`feather-${allocationAvailable < 0 ? 'alert-triangle' : 'check-circle'}`} 
                           style={{ 
                             fontSize: '20px',
                             color: allocationAvailable < 0 ? '#dc3545' : '#28a745'
                           }}></i>
                      </div>
                      <div>
                        <div className="text-muted mb-1" style={{ fontSize: '12px', fontWeight: '500' }}>
                          {allocationAvailable < 0 ? 'Oversubscribed' : 'Allocation Available'}
                        </div>
                        <div className={`fw-bold ${allocationAvailable < 0 ? 'text-danger' : 'text-success'}`} style={{ fontSize: '18px' }}>
                          {allocationAvailable.toLocaleString()} {project.allocation_currency || 'USDT'}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Allocation Details Card */}
            <div className="card shadow-sm border-0">
              <div className="card-header bg-white border-bottom d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-bold">
                  <span className="text-danger">{project.name.toUpperCase()}</span> Allocation Details
                </h6>
                <div className="d-flex gap-2">
                  <button 
                    type="button" 
                    className="btn btn-warning btn-sm"
                    onClick={ascendingDistribution}
                  >
                    <i className="feather-arrow-up me-1"></i>Ascending Distribution
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-danger btn-sm"
                    onClick={divideEqually}
                  >
                    <i className="feather-align-justify me-1"></i>Equal Distribution
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead style={{ backgroundColor: '#f8f9fa' }}>
                      <tr>
                        <th className="border-0 px-3 py-2 fw-semibold text-dark fs-12 text-center" style={{ width: '80px' }}>S. No.</th>
                        <th className="border-0 px-3 py-2 fw-semibold text-dark fs-12 text-center">Name</th>
                        <th className="border-0 px-3 py-2 fw-semibold text-dark fs-12 text-center">Amount</th>
                        <th className="border-0 px-3 py-2 fw-semibold text-dark fs-12 text-center">Allocated Amt.</th>
                        <th className="border-0 px-3 py-2 fw-semibold text-dark fs-12 text-center">% Allocation</th>
                        <th className="border-0 px-3 py-2 fw-semibold text-dark fs-12 text-center">Wallet Address</th>
                      </tr>
                    </thead>
                    <tbody>
                      {interests.map((interest, index) => (
                        <tr key={interest.participant_id || index} className="border-bottom">
                          <td className="px-3 py-2 text-center">
                            <span className="fw-semibold text-dark fs-12">{index + 1}</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="fw-medium text-dark fs-12">{interest.participant_name}</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="fw-semibold text-dark fs-12">{interest.amount || 0}</span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <input
                              type="number"
                              className="form-control form-control-sm text-center"
                              value={interest.allocation_amount || 0}
                              onChange={(e) => handleAllocationChange(interest.participant_id, e.target.value)}
                              style={{ maxWidth: '150px', margin: '0 auto' }}
                            />
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="fw-medium text-dark fs-12">
                              {totalAllocation > 0 
                                ? ((parseInt(interest.allocation_amount || 0) * 100 / totalAllocation).toFixed(3))
                                : '0.000'
                              }%
                            </span>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span className="fw-medium text-dark fs-12" style={{ wordBreak: 'break-all' }}>
                              {interest.wallet_address && interest.wallet_address !== 'undefined' ? interest.wallet_address : '-'}
                            </span>
                          </td>
                        </tr>
                      ))}
                      {interests.length === 0 && (
                        <tr>
                          <td colSpan="6" className="text-center py-4 text-muted">
                            <i className="feather-inbox fs-1 mb-2 d-block"></i>
                            No interest details found
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Action Buttons */}
                {interests.length > 0 && (
                  <div className="d-flex gap-2 justify-content-end mt-4">
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={makeActive}
                      disabled={isMakingActive}
                    >
                      <i className="feather-check me-1"></i>
                      {isMakingActive ? 'Activating...' : 'Make Active'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary btn-sm"
                      onClick={saveAllocation}
                      disabled={isSaving}
                    >
                      <i className="feather-save me-1"></i>
                      {isSaving ? 'Saving...' : 'Update'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-warning btn-sm"
                      onClick={() => window.location.href = `/upcoming?${project.name}`}
                    >
                      <i className="feather-x me-1"></i>Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AllocationSheet;

